import { Drawer, TextInput } from "@caprikorn/dynamo-ui";
import { type FC, useId } from "react";
import { Controller, useForm } from "react-hook-form";

import { usePatchUserNames } from "@services/apiEcms/usePatchUserNames";
import { emailRegex } from "@utils/form";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./IdentityDrawer.module.css";
import type { IdentityDrawerProps, IdentityForm } from "./IdentityDrawer.types";

export const IdentityDrawer: FC<IdentityDrawerProps> = ({
  userIdentityData,
  userId,
  onClose,
}) => {
  const { t } = useCustomTranslation(["main", "common"]);
  const formId = useId();

  const { mutate: updateUserNames, isPending } = usePatchUserNames();

  const onSubmitIdentity = (data: IdentityForm) => {
    updateUserNames(
      { id: userId, firstName: data.firstName, lastName: data.lastName },
      {
        onSuccess: onClose,
      },
    );
  };

  const { control, handleSubmit } = useForm<IdentityForm>({
    values: {
      email: userIdentityData.email,
      firstName: userIdentityData.firstName,
      lastName: userIdentityData.lastName,
    },
  });

  return (
    <Drawer data-testid="drawer-edit-user-drawer" onClose={onClose}>
      <Drawer.Header title={t("userDetails.informations.drawer")} />
      <Drawer.Body>
        <form
          className={classes.form}
          id={formId}
          onSubmit={handleSubmit(onSubmitIdentity)}
        >
          <Controller
            control={control}
            name="lastName"
            render={({ field, fieldState }) => (
              <TextInput
                className={classes.input}
                hasError={Boolean(fieldState.error)}
                hint={
                  fieldState.error?.message &&
                  t(fieldState.error.message, { ns: "common" })
                }
                label={t("userDetails.informations.labels.lastName")}
                size="lg"
                {...field}
              />
            )}
            rules={{ required: "form.error.required" }}
          />
          <Controller
            control={control}
            name="firstName"
            render={({ field, fieldState }) => (
              <TextInput
                className={classes.input}
                hasError={Boolean(fieldState.error)}
                hint={
                  fieldState.error?.message &&
                  t(fieldState.error.message, { ns: "common" })
                }
                label={t("userDetails.informations.labels.firstName")}
                size="lg"
                {...field}
              />
            )}
            rules={{ required: "form.error.required" }}
          />
          <Controller
            control={control}
            name="email"
            render={({ field, fieldState }) => (
              <TextInput
                className={classes.input}
                disabled
                hasError={Boolean(fieldState.error)}
                hint={
                  fieldState.error?.message &&
                  t(fieldState.error.message, { ns: "common" })
                }
                label={t("form.email.label", { ns: "common" })}
                size="lg"
                type="email"
                {...field}
              />
            )}
            rules={{
              required: "form.error.required",
              pattern: {
                value: emailRegex,
                message: "form.error.invalidFormat",
              },
            }}
          />
        </form>
      </Drawer.Body>
      <Drawer.Footer>
        <Drawer.Footer.ConfirmButton
          form={formId}
          isLoading={isPending}
          type="submit"
        />
        <Drawer.Footer.CancelButton />
      </Drawer.Footer>
    </Drawer>
  );
};
